import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { VinconexLogo } from "@/components/VinconexMark";
import { MfaCodeStep } from "@/components/MfaCodeStep";
import { adminHref } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState(null);

  const goIn = (u) => navigate(u?.platform_admin && !u?.dealer_id ? "/dashboard/platform" : "/dashboard", { replace: true });

  useEffect(() => {
    if (user) navigate((user.platform_admin || user.platform_owner) && !user.dealer_id ? "/dashboard/platform" : "/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password, "dealer");
    setLoading(false);
    if (res.ok) goIn(res.user);
    else if (res.mfa) setMfa(res.mfa);
    else setError(res.error);
  };

  return (
    <div className="min-h-screen bg-[#05070C] grid-noise flex flex-col">
      <div className="p-5 sm:p-8">
        <VinconexLogo variant="wordmark" className="h-6 w-auto" />
      </div>
      <div className="flex-1 flex items-center justify-center px-5 pb-20">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
          <div className="flex justify-center mb-6">
            <VinconexLogo variant="mark" className="h-16 w-auto" to={null} />
          </div>
          <div className="tracing-top rounded-2xl border border-[#1C2233] bg-[#0B0F18] p-8">
            {mfa ? (
              <MfaCodeStep challenge={mfa} onVerified={goIn} onBack={() => { setMfa(null); setPassword(""); }} />
            ) : (
            <>
            <div className="inline-flex items-center gap-2 text-[#0A8CF2] text-xs font-mono-tech uppercase tracking-widest mb-3">
              <Lock className="h-3.5 w-3.5" /> Dealer access
            </div>
            <h1 className="font-heading text-2xl font-extrabold text-white mb-1">Sign in</h1>
            <p className="text-sm text-slate-400 mb-6">Dealership admins &amp; managers.</p>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-slate-300 mb-1.5 block">Email</Label>
                <Input id="email" data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@dealership.com" className="bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100 placeholder:text-slate-600" />
              </div>
              <div>
                <Label htmlFor="password" className="text-slate-300 mb-1.5 block">Password</Label>
                <Input id="password" data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" className="bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100 placeholder:text-slate-600" />
              </div>
              {error && (
                <div data-testid="login-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900/50 rounded-md px-3 py-2">{error}</div>
              )}
              <Button type="submit" data-testid="admin-login-submit" disabled={loading} className="w-full h-11 bg-[#0A8CF2] hover:bg-[#3AA3FF] text-white font-bold shadow-lg shadow-blue-900/40">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
              </Button>
            </form>
            </>
            )}
          </div>
          <p className="text-center text-sm text-slate-500 mt-5">
            New dealership?{" "}
            <Link to="/signup" data-testid="login-signup-link" className="text-[#7FC3FF] hover:text-white font-semibold">Add yours to Vinconex</Link>
          </p>
          <p className="text-center text-xs text-slate-600 mt-3">
            Vinconex staff? <a href={adminHref()} data-testid="login-admin-link" className="text-slate-400 hover:text-white underline underline-offset-2">Admin console sign-in</a>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
